import { useEffect, useState } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../config/axiosInstance';

const LoginPasswordModal = ({ user, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState('');
  const [revealing, setRevealing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNewPassword('');
    setConfirmPassword('');
    setRevealedPassword('');
  }, [user]);

  if (!user) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (newPassword.length < 8 || newPassword.length > 128) return toast.error('Password must be between 8 and 128 characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    try {
      setSaving(true);
      const response = await axiosInstance.put(`/api/admin/users/${user.id}/login-password`, { newPassword, confirmPassword });
      setRevealedPassword(response.data?.data?.password || newPassword);
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Login password changed successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change login password');
    } finally {
      setSaving(false);
    }
  };

  const toggleCurrentPassword = async () => {
    if (revealedPassword) return setRevealedPassword('');
    try {
      setRevealing(true);
      const response = await axiosInstance.get(`/api/admin/users/${user.id}/login-password`);
      setRevealedPassword(response.data?.data?.password || '');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reveal login password');
    } finally {
      setRevealing(false);
    }
  };

  const field = (label, value, setValue, visible, setVisible) => (
    <div className='mb-3'>
      <label className='form-label fw-semibold'>{label}</label>
      <div className='input-group'>
        <input type={visible ? 'text' : 'password'} className='form-control' value={value} onChange={(e) => setValue(e.target.value)} minLength={8} maxLength={128} autoComplete='new-password' disabled={saving} />
        <button type='button' className='btn btn-outline-secondary' onClick={() => setVisible(!visible)} aria-label={`Toggle ${label.toLowerCase()} visibility`}>
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className='modal d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} onClick={onClose}>
      <div className='modal-dialog modal-dialog-centered' onClick={(e) => e.stopPropagation()}>
        <form className='modal-content border-0 shadow-lg' onSubmit={submit}>
          <div className='modal-header text-white border-0' style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <h5 className='modal-title d-flex align-items-center gap-2'><Lock size={22} />Change Login Password</h5>
            <button type='button' className='btn-close btn-close-white' onClick={onClose} />
          </div>
          <div className='modal-body p-4'>
            <div className='alert alert-light border'><strong>{user.fullName || 'Unnamed user'}</strong><br /><small className='text-muted'>{user.email}</small></div>
            <div className='mb-3'>
              <label className='form-label fw-semibold'>Current Login Password</label>
              <div className='input-group'>
                <input className='form-control font-monospace' type={revealedPassword ? 'text' : 'password'} value={revealedPassword || '••••••••'} readOnly aria-label='Current login password' />
                <button type='button' className='btn btn-outline-secondary' onClick={toggleCurrentPassword} disabled={saving || revealing} aria-label={revealedPassword ? 'Hide current login password' : 'Reveal current login password'} title={revealedPassword ? 'Hide' : 'Reveal'}>
                  {revealedPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <small className='text-muted'>{revealing ? 'Revealing...' : revealedPassword ? 'Visible to authorized administrators.' : 'Hidden by default.'}</small>
            </div>
            {field('New Password', newPassword, setNewPassword, showNew, setShowNew)}
            {field('Confirm New Password', confirmPassword, setConfirmPassword, showConfirm, setShowConfirm)}
            <small className='text-muted'>Use 8 to 128 characters.</small>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-secondary' onClick={onClose} disabled={saving}>Cancel</button>
            <button type='submit' className='btn btn-primary' disabled={saving}>{saving ? 'Changing...' : 'Change Password'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPasswordModal;
